import { t, Trans } from "@lingui/macro";
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
    Table,
    Button,
    Row,
    Col,
    Card,
    CardHeader,
    CardFooter,
    CardBody,
    CardTitle,
    CardText,
    Badge
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';

import useAccessCheck, { checkAccess } from '../../util/useAccessCheck';
import useResourceLoader from '../../util/useResourceLoader';
import UWEEntityApi from '../../api/UWEEntityApi';
import UweApi from '../../api/UweApi';
import Loader from '../Loader';
import Notification from '../Notification';
import useTitle from '../../util/useTitle';
import Pagination from '../Pagination';
import useSearchParams from '../../util/useSearchParams';
import SearchBar from "../Search/SearchBar";
import SortButton from "../SortButton";
import { AuthContext } from '../../context/AuthContext';
import { getObject, interpolate, mapObject } from '../../util/mapObject';
import WorkTrayIcon from './WorkTrayIcon';
import parseFormDefinition from '../../util/parseFormDefinition';
import ElementIcon from '../ElementIcon';
import { concatRoute, interpolateRoute, useRouteInterpolation } from '../../util/routeUtil';
import { IDENTITY_FORMATTER, useFormatter } from "../../util/applyFormatting";
import { useJnx, useMultipleJnx } from "../../util/jnx";
import useOptionsLookup, { computeLookupParams } from "../customFields/hooks/useOptionsLookup";
import { useOpenModal } from "../FormModal";
import ModalContainer from "../ModalContainer";
import Diagram from "./Diagram";
import useFilteredProps from "../../hooks/useFilteredProps";

function WorkTray(props) {
    const { element, scope, selected } = props;

    const history = useHistory()

    if(element.type === "worktray")
        useTitle(element.title);

    const auth = useContext(AuthContext);
    const {
        user: {
            user: { role: { extras } = {}, persistFilters } = {},
        } = {}
    } = auth;

    const defaultView = useMemo(() => extras ? JSON.parse(extras).defaultView : null, [extras]);
    const exportableByEmail = element.exportByEmail;
    const exportable = element.columns.some(column => {
        if (column.exportable) {
            return true;
        }
        return false;
    })
    const headerCols = exportable ? 9 : 11;

    const [formDefinition, sFilters] = parseSearchFormDefinition(element);
    const [filters, setFilters] = useState(sFilters);
    const [showFilter, setShowFilter] = useState(false);
    const [reportEnabled, setReportEnabled] = useState(true);

    useEffect(() => setFilters({ ...(filters || {}), ...(defaultView || {}) }), [defaultView]);

    const toggleFilter = (e) => {
        e.preventDefault();
        setShowFilter(!showFilter);
    }

    const exportTray = async (e) => {
        e.preventDefault();
        if (!reportEnabled) {
            return;
        }
        setReportEnabled(false);
        if(exportableByEmail){
            const resource = `uwe-entities/${lookup.entity}/${scope.dashboard.id}/asynchronous-report`;
            setTimeout(() => { setReportEnabled(true); }, (5000));
            await UWEEntityApi.GenerateAsynchronousReport({ resource, allRows:true, sortBy, ...filters });
        } else {
        const exportableColumns = element.columns.filter((column) => column.exportable);
        const resource = lookup.entity ? `uwe-entities/${lookup.entity}/list` : lookup.api.split(':')[1];
        if (!resource) return null;
        mergeParamsIntoFilters(filters, lookupParams);
        const data = await UWEEntityApi.getWorkTray({ resource, allRows:true, sortBy, ...filters });
        const exportDataArray = data.items.map((item) =>{
            return exportableColumns.reduce((acc, column) => {
                const result = column.sortkey?.split('.').reduce((prev, curr) => {
                    return prev ? prev[curr] : null
                }, item)
                acc[column.title] = result;
                return acc;
            }, {})
        })
        if (Array.isArray(exportDataArray)) {
            const csvStr = JSONtoCSV(exportDataArray);
            downloadCSV(csvStr);
        }
        setReportEnabled(true);
        }
    }

    function JSONtoCSV(jsonArray, headers) {
        if (jsonArray === null || jsonArray === undefined) { return null };
        const replacer = (key, value) => value === null ? '' : value
        const header = Object.keys(jsonArray[0])
        let csv = jsonArray.map(row => header.map(fieldName =>
            JSON.stringify(row[fieldName], replacer)).join(','))
        csv.unshift(header.join(','))
        csv = csv.join('\r\n')
        return csv;
    }

    function downloadCSV(csvStr) {

        const hiddenElement = document.createElement('a');
        const universalBOM = "\uFEFF";
        const blobData = new Blob([universalBOM + csvStr], { type: 'text/csv' });
        const blobURL = URL.createObjectURL(blobData);
        hiddenElement.href = blobURL;
        hiddenElement.target = '_blank';
        hiddenElement.download = `report.csv`;
        hiddenElement.click();
    }

    const {
        lookup,
        isSelected, setSelected,
    } = useWorktrayHooks(props);

    // localStorage.removeItem(`${useWorktrayHooks(props).lookup.entity}-params`);
    const isFirstLoad = useRef(true);
    const localStorageKey = `${lookup.entity}-params`;
    const localStorageItem = localStorage.getItem(localStorageKey);
    const storedParams = useMemo(() => JSON.parse(localStorageItem || "{}"), [localStorageItem]);

    // Helper: always read the latest saved params to avoid stale closure overwrites
    const getLatestParams = () => JSON.parse(localStorage.getItem(localStorageKey) || "{}");
    
    const formatearFecha =  (fecha) => {
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;

            let allFilters = persistFilters ? (storedParams.filters || {}) : {};

            if (persistFilters) {
                setShowFilter(!!storedParams.filters);
            } else {
                localStorage.removeItem(`${lookup.entity}-params`);
            }

            if(storedParams.filters === undefined &&  ['TravelLog','TravelApplication'].includes(props.element.entityType)){
                if (props.element.filters && props.element.filters.some(x => x.key === 'after') && !allFilters.after) {
                    allFilters = {
                        ...allFilters,
                        after: formatearFecha(new Date(Date.now() -30  * 24 * 60 * 60 * 1000))
                    };
                }

                if (props.element.filters && props.element.filters.some(x => x.key === 'before') && !allFilters.before) {
                    allFilters = {
                        ...allFilters,
                        before: formatearFecha(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
                    };
                }
            }

            setFilters(allFilters);
        }
    }, [storedParams]);
    // Read fresh from localStorage at mount time so we don't inherit stale values from a prior worktray
    const [sortBy, setSortByValue] = useState(() => JSON.parse(localStorage.getItem(localStorageKey) || "{}").sortBy || element.defaultFilter);
    const [offset, setOffsetValue] = useState(() => JSON.parse(localStorage.getItem(localStorageKey) || "{}").offset || 0);
    const [size, setSizeValue] = useState(() => JSON.parse(localStorage.getItem(localStorageKey) || "{}").size || 10);
    
    const setOffset = (newOffset) => {
        setOffsetValue(newOffset);
        // Read fresh params to avoid overwriting changes made by other setters
        const updatedParams = { ...getLatestParams(), offset: newOffset };
        localStorage.setItem(localStorageKey, JSON.stringify(updatedParams));
    };

    const setSize = (newSize) => {
        setSizeValue(newSize);
        // Reset offset when page size changes so we don't end up with an invalid offset
        setOffsetValue(0);
        const updatedParams = { ...getLatestParams(), size: newSize, offset: 0 };
        localStorage.setItem(localStorageKey, JSON.stringify(updatedParams));
    };

    const setSortBy = (newSortBy) => {
        setSortByValue(newSortBy);
        // Reset offset when sort changes to avoid "offset may not be negative" errors
        setOffsetValue(0);
        const updatedParams = { ...getLatestParams(), sortBy: newSortBy, offset: 0 };
        localStorage.setItem(localStorageKey, JSON.stringify(updatedParams));
    };

    const [loaderFnAction, setLoaderFnAction] = useState(false);

    const openModal = useOpenModal();

    function mergeParamsIntoFilters(filters, params) {
        if (!params || typeof params !== "object") {
            return filters;
        }

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                if(value.length === 2 ){
                    if(value[1])
                        filters[value[0]] = value[1];
                }
                    else
                filters[key] = value.filter(Boolean).map(String).join(",");
            } else if (value != null && typeof value !== "object")
                filters[key] = String(value);
        });

        return filters;
    }

    const strFilters = useMemo(() => {

        if (!filters) return;
        return JSON.stringify(filters);

    }, [filters]);


     const paramsJnx = useMultipleJnx(lookup.params);
    const [lookupParams,lookupParamsJSON] = useMemo(() => {
        const params = computeLookupParams(scope?.selected, lookup.params, paramsJnx, scope);
        return [params,JSON.stringify(params)];
    }, [lookup?.params, scope]);

    const [paging, loading, error] = useResourceLoader(async () => {
        const resource = lookup.entity ? `uwe-entities/${lookup.entity}/list` : lookup.api.split(':')[1];
        if (!resource) return null;
        mergeParamsIntoFilters(filters, lookupParams);
        const data = await UWEEntityApi.getWorkTray({ resource, offset, size, sortBy, ...filters });
        return Array.isArray(data) ? {
            from: 0,
            to: data.length,
            count: data.length,
            items: data
        } : data;
    }, [size, offset, sortBy, lookup, strFilters, lookupParamsJSON]);

    const {
        from = 0,
        to = 0,
        count = 0,
        items: data = [],
    } = paging || {};


    const onSearch = (update) => {
        setFilters({ ...filters, ...update });
        setOffsetValue(0);
        const updatedParams = {
            ...getLatestParams(),
            offset: 0, 
            filters: {...filters, ...update},
        };
        localStorage.setItem(localStorageKey, JSON.stringify(updatedParams));
    }

    const onClearSearch = () => {
        setFilters({});
        setOffsetValue(0);
        localStorage.removeItem(localStorageKey);
    };

    const [tab, setTab] = useState("table");

    return (
        <div className={`worktray ${element[":className"]}`} onClick={e => { e.preventDefault() }} role="table">
            <ModalContainer />
            {element.canCreate ? <WorkTrayIcon element={element.canCreate} /> : null}
            <Card className={`inbox-card primary ${element.filters && showFilter ? '' : 'no-bottom-gap'}`}>
                <CardHeader>
                    <Row>
                        <Col {...element.tableActions ? { xs: headerCols, sm: headerCols } : { xs: headerCols }}>
                            <div className={`title ${element.tableActions ? 'has-buttons' : ''}`}>{element.title}</div>
                            {element.tableActions ? (
                                <ActionButtonsCell actions={element.tableActions} scope={scope} />
                            ) : null}
                            {element.canSeeDiagram ? (
                                <div className="float-right">
                                    {tab !== "tree" ? <Button color="secondary" onClick={() => setTab("tree")} ><i className="fa fa-network-wired" /> Ver Diagrama</Button> : <Button color="secondary" onClick={() => setTab("table")} ><i className="fa fa-table" /> Ver Tabla</Button>}
                                </div>
                            ) : null}
                        </Col>
                        {exportable ? (<Col sm="2"><div className="float-right hide-when-mobile">
                            <Button onClick={exportTray} type="button" outline color="secondary"  active={reportEnabled}>
                                {
                                    reportEnabled ? (
                                    <div>Exportar Reporte</div>
                                    ) : (
                                        exportableByEmail ? 
                                        <div><Loader centered sm>Se está generando un reporte de los expedientes. Recibirá el reporte por email en unos minutos. Agradecemos su paciencia.</Loader></div> :
                                        <div><Loader centered sm>Exportando...</Loader></div>
                                    )
                                }
                            </Button>
                        </div></Col>) : null}
                        {element.filters ? (<Col sm="1"><div className="float-right">
                            <Button onClick={toggleFilter} type="button" outline color="secondary" active={showFilter}>
                                <i className="glyphicon glyphicon-filter" />
                            </Button>
                        </div></Col>) : null}

                    </Row>
                </CardHeader>
                {showFilter ? <SearchBar
                    formDefinition={formDefinition}
                    filters={filters}
                    onSearch={onSearch}
                    onClearSearch={onClearSearch}
                />
                    : null}
            </Card>
            <Card className={`inbox-card ${showFilter ? '' : 'square-top'}`}>
                {element.canSeeDiagram && tab === "tree" ?
                    <Diagram data={data} dataDiagram={element.canSeeDiagramnt} history={history} />
                    :
                    <>
                        <Table className="hide-when-mobile">
                            {/* <thead><tr>{element.columns.map(({ sortkey, title, style, headerStyle }, idx) => 
                                sortkey ? (
                                <SortButton key={idx}
                                    tag="th" sortKey={sortkey} sortBy={sortBy} setSortBy={setSortBy}
                                    style={style || headerStyle}
                                >{title}</SortButton>
                            ) : (
                                <th key={idx} style={style || headerStyle} >{title}</th>
                            ))}</tr></thead> */}
                            <thead><tr>{element.columns.reduce((acc, { sortkey, title, style, headerStyle }, idx) => {
                                if (!element.columns[idx].hideColumn) {
                                    acc.push(sortkey ? (
                                        <SortButton key={idx}
                                            tag="th" sortKey={sortkey} sortBy={sortBy} setSortBy={setSortBy}
                                            style={style || headerStyle}
                                        >{title}</SortButton>
                                    ) : (
                                        <th key={idx} style={style || headerStyle} >{title}</th>
                                    ))
                                }
                                return acc;
                            }, [])}</tr></thead>
                            <tbody>{loading ? (
                                <tr><td colSpan="8">
                                    <Loader centered><Trans>Loading {element.title}</Trans></Loader>
                                </td></tr>
                            ) : (<>
                                {error ? (<tr><td colSpan="8">
                                    <Notification error={error} />
                                </td></tr>) : null}
                                {(data && data.length) ? data.map((rowObject, idx) => (
                                    <DataRow key={idx} columns={element.columns} rowObject={rowObject} scope={scope}
                                        selected={isSelected(rowObject)}
                                        setSelected={setSelected}
                                        openModal={openModal}
                                        onSearch={onSearch}
                                        loaderFnAction={setLoaderFnAction}
                                    />
                                )) : (<tr><td colSpan="7">
                                    <Notification color="warning"><Trans>The list of {element.title} is empty.</Trans></Notification>
                                </td></tr>)}
                            </>)}</tbody>
                        </Table>
                        <div className="mobile-table show-when-mobile">
                            {loading ? (
                                <Loader centered><Trans>Loading {element.title}</Trans></Loader>
                            ) : (<>
                                {(data && data.length) ? data.map((rowObject, idx) => (
                                    <MobileDataRow key={idx} columns={element.columns} rowObject={rowObject} scope={scope}
                                        selected={isSelected(rowObject)}
                                        setSelected={setSelected}
                                    />
                                )) : (
                                    <Notification color="warning"><Trans>The list of {element.title} is empty.</Trans></Notification>
                                )}
                            </>)}
                        </div>
                        <CardFooter className="pagination">
                            <div className="float-right">
                                <Pagination offset={from} count={count} size={size} setSize={setSize} setOffset={setOffset} />
                            </div>
                        </CardFooter>
                    </>
                }
            </Card>
        </div >
    );
}

function useWorktrayHooks({
    element,
    selected: propSelected,
    setSelected: propSetSelected
}) {
    const {
        selectable,
    } = element;
    const lookup = useMemo(() => element.lookup || ({
        entity: element.entityType,
        api: !element.entityType && element.resource ? `api:${element.resource}` : null,
        params: element.lookupParams
    }), [element?.lookup, element]);

    const idAttr = lookup.id;

    const { isSelected, setSelected } = useMemo(() => {
        let isSelected = () => false;
        let setSelected;

        if (selectable) {
            const valueFn = idAttr ? (x => x[idAttr]) : (x => x);
            isSelected = x => {
                const xValue = valueFn(x);
                return xValue === propSelected;
            };
            setSelected = x => propSetSelected(valueFn(x));
        }

        return { isSelected, setSelected };
    }, [selectable, idAttr, propSelected]);

    return {
        lookup,
        isSelected, setSelected
    };
}

WorkTray.rootSchema = {
    "definitions": {
        "mapFieldField": {
            "type": ["string", "object"],
            "ui:field": "JsonField",
            "showTree": true,
        },
        "requireRestrictfield": {
            "type": "array",
            ":classNames": "from-col-1-size-2",
            "ui:arrayType": "cards",
            "items": {
                "type": "object",
                "ui:addButtonText": "Add Option",
                "ui:positionButtons": "top-outside",
                "properties": {
                    "flag": {
                        "title": t`User Flag`,
                        "type": "string"
                    },
                    "role": {
                        "title": t`Role`,
                        "type": "string",
                        "ui:field": "LookupFormField",
                        "lookup": {
                            "api": 'roles',
                            "resource": t`Roles`,
                            "id": 'name',
                            "label": 'name'
                        }
                    },
                    "permission": {
                        "title": t`Permission`,
                        "type": "string",
                        "enum": ["canWorkAs", "canBeAssigned", "canAssign"],
                        "enumNames": [t`Can Work`, t`Can be Assigned`, t`Can Assign`]
                    }
                },
                "ui:field": "ToggleObjectField"
            }
        },
        "actionsField": {
            "type": "array",
            "title": "Actions",
            "ui:arrayType": "cards",
            "ui:newRow": true,
            "items": {
                "type": "object",
                "ui:addButtonText": "Add Action",
                "ui:positionButtons": "top",
                "ui:sectionType": "subsubsection",
                "properties": {
                    "obj": {
                        "type": "object",
                        ":classNames": "grid-2-columns",
                        "title": [
                            "# ${$index + 1}: ${label}",
                        ].join(''),
                        "ui:newSection": true,
                        "ui:expandable": true,
                        "properties": {
                            "label": {
                                "title": "Action",
                                "type": "string",
                                "map:field": "label"
                            },
                            "backgroundColor": {
                                "title": "Color",
                                "type": "string",
                                "map:field": "backgroundColor"
                            },
                            "openInNewTab": {
                                "title": "Open In New Tab",
                                "type": "string",
                                "map:field": "openInNewTab"
                            },
                            "route": {
                                "title": "Route",
                                "type": "string",
                                "map:field": "route"
                            },
                            "requireExpr": {
                                "title": "requireExpr",
                                "type": ["string", "object"],
                                "ui:field": "JsonField",
                                "map:field": "requireExpr"
                            },
                            "requirerestrict": {
                                "type": "object",
                                ":classNames": "from-col-1-size-2",
                                "title": "Conditions",
                                "ui:sectionType": "label",
                                "ui:field": "ToggleObjectField",
                                "properties": {
                                    "require": {
                                        "title": "Require Any",
                                        "map:field": "require",
                                        "$$include": "#/definitions/requireRestrictfield",
                                    },
                                    "restrict": {
                                        "title": "Restrict If Any",
                                        "map:field": "restrict",
                                        "$$include": "#/definitions/requireRestrictfield",
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    "properties": {
        "title": {
            "title": "Title",
            "type": "string",
            "map:field": "title"
        },
        "className": {
            "title": "Class Names",
            "type": "string",
            "map:field": ":classNames"
        },
        "entityType": {
            "title": "Entity Type",
            "type": "string",
            "map:field": "entityType",
            "ui:field": "LookupFormField",
            "lookup": {
                "resource": "Entity Types",
                "api": "api:manage/objectschemas",
                "params": { "all": true },
                "options": { "useCache": true },
                "jnx": "data.items",
                "id": "name",
                "label": "name"
            }
        },
        "resource": {
            "title": "Resource",
            "type": "string",
            "map:field": "resource"
        },
        "lookupParams": {
            "title": "Params",
            "type": "object",
            "map:field": "lookupParams",
            "properties": {},
            "additionalProperties": {
                "type": "string",
                "title": "value"
            },
            "ui:asPropertiesTable": true
        },
        "tableActions": {
            "$$include": "#/definitions/actionsField",
            ":classNames": "from-col-1-size-3",
            "map:array": "tableActions"
        },
        "defaultFilter": {
            "title": "Default Filter",
            "type": "string",
            "map:field": "defaultFilter"
        },
        "filters": {
            "title": "Filters",
            ":classNames": "from-col-1-size-3",
            "type": "array",
            "map:array": "filters",
            "items": {
                "type": "object",
                "properties": {
                    "title": {
                        "type": "string",
                        "title": "title",
                        "map:field": "title"
                    },
                    "key": {
                        "type": "string",
                        "title": "key",
                        "map:field": "key"
                    },
                    "ui:field": {
                        "type": "string",
                        "title": "Tipo de Campo",
                        "map:field": "ui:field"
                    },
                    "lookup": {
                        "type": "string",
                        "ui:field": "JsonField",
                        "title": "lookup",
                        "map:field": "lookup"
                    },
                    "type": {
                        "type": "string",
                        "title": "type",
                        "map:field": "type"
                    },
                    "format": {
                        "type": "string",
                        "title": "format",
                        "map:field": "format"
                    },
                }
            }
        },
        "columns": {
            "title": "Columns",
            ":classNames": "from-col-1-size-3",
            "type": "array",
            "map:array": "columns",
            "items": {
                "type": "object",
                "ui:showColumnsIf": {
                    "actions": { "expr": "hasActions", "debug": "actions showColumnsIf", "scope": ".." }
                },
                "properties": {
                    "title": {
                        "type": "string",
                        "map:field": "title",
                        "title": "Title"
                    },
                    "sortkey": {
                        "type": "string",
                        "map:field": "sortkey",
                        "title": "Key"
                    },
                    "mapField": {
                        "$$include": "#/definitions/mapFieldField",
                        "map:field": "map:field",
                        "title": "Field"
                    },
                    "format": {
                        "type": "string",
                        "map:field": "format",
                        "title": "Format"
                    },
                    "hideColumn": {
                        "type": "boolean",
                        "map:field": "hideColumn",
                        "title": "Hide"
                    },
                    "exportable": {
                        "type": "boolean",
                        "map:field": "exportable",
                        "title": "Exportable"
                    },
                    "panel2": {
                        "ui:newRow": true,
                        "type": "object",
                        "title": "Extra Properties...",
                        "ui:field": "HideObjectField",
                        "ui:newSection": true,
                        "ui:sectionType": "subsubsection",
                        "ui:expandable": true,
                        "ui:expandedDefault": false,
                        ":classNames": "grid-2-columns bordered",
                        "properties": {
                            "color": {
                                "$$include": "#/definitions/mapFieldField",
                                "map:field": "color",
                                "title": "Color"
                            },
                            "valueTranform": {
                                "$$include": "#/definitions/mapFieldField",
                                "map:field": "valueTranform",
                                "title": "Value Tranform Expression"
                            },
                            "mapImage": {
                                "$$include": "#/definitions/mapFieldField",
                                ":classNames": "from-col-1-size-1",
                                "map:field": "map:image",
                                "title": "Image"
                            },
                            "iconSize": {
                                "title": "Icon Size",
                                "ui:showIf": { "scope": ".", "expr": "$isTruthy(mapImage)", debug: "iconSize showIf" },
                                "type": "string",
                                "enum": ["1em", "2em", "4em", "6em", "8em", "10em"],
                                "map:field": { "default": "icons.size", "path": "fontSize" }
                            },
                            "type": {
                                "$$include": "#/definitions/mapFieldField",
                                "map:field": "type",
                                "title": "Type"
                            },
                            "actions": {
                                "$$include": "#/definitions/actionsField",
                                ":classNames": "from-col-1-size-2",
                                "map:array": "actions"
                            }
                        }
                    },
                }
            }
        },
    }
}


function DataRow({ rowObject, columns, scope, selected, setSelected, openModal, onSearch, loaderFnAction }) {

    const fnBindings = {
        onSearch,
        openModal,
        loaderFnAction
    };

    return (<tr>{columns.reduce((acc, cell, idx) => {
        if (!cell.hideColumn) {
            acc.push(
                <td key={idx} style={cell.style}>{cell.actions && cell.actions.length >0  ? (
                    <ActionButtonsCell fnBindings={fnBindings} actions={cell.actions} rowObject={rowObject} scope={scope} />
                ) : (
                    <DataCell cell={cell} rowObject={rowObject}
                        selected={selected}
                        setSelected={setSelected}
                    />
                )}</td>
            )
        }
        return acc;
    }, [])}</tr>);

    // return (<tr>{columns.map((cell, idx) => {
    //     return (<td key={idx} style={cell.style}>{cell.actions ? (
    //         <ActionButtonsCell fnBindings={fnBindings} actions={cell.actions} rowObject={rowObject} scope={scope} />
    //     ) : (
    //         <DataCell cell={cell} rowObject={rowObject}
    //             selected={selected}
    //             setSelected={setSelected}
    //         />
    //     )}</td>);
    // })}</tr>);
}

function MobileDataRow({ rowObject, columns, scope, selected, setSelected }) {
    return (<Card>
        <CardBody>{columns.reduce((acc, cell, idx) => {
            if (!cell.hideColumn === true) {
                acc.push(
                    (<>{cell.actions ? (
                        <ActionButtonsCell actions={cell.actions} rowObject={rowObject} scope={scope} />
                    ) : (
                        <CardText>
                            <MobileDataCell cell={cell} rowObject={rowObject}
                                selected={selected}
                                setSelected={setSelected}
                            />
                        </CardText>
                    )
                    }</>)
                )
            }
            return acc
        }, [])}</CardBody>
    </Card>);
}

function MobileDataCell({ rowObject, cell, selected, setSelected }) {
    const {
        'map:field': mapField,
        'map:image': mapImage,
        iconSize,
        format,
        type,
        checkbox,
        color: colorExpr,
        title
    } = cell;
    const emptyText = cell.emptyText || (checkbox ? '' : '---');

    const colorJnx = useJnx(colorExpr);

    const value = useMemo(() => mapField ? mapObject(rowObject, { "value": mapField }).value : null, [rowObject, mapField]);
    const color = useMemo(() => colorJnx?.eval(rowObject, '', { value }), [value, colorJnx, rowObject]);
    const formatter = useFormatter(format);
    const text = useMemo(() => (
        formatter !== IDENTITY_FORMATTER ? (
            formatter.apply(value)
        ) : (value !== null && value !== undefined ? (
            `${value}`
        ) : ''
        )), [value, formatter]);
    const image = useMemo(() => mapImage ? mapObject(rowObject, { "value": mapImage }).value : null, [rowObject, mapImage]);

    const onClick = useCallback((e) => {
        e.preventDefault();
        setSelected(rowObject);
    }, [rowObject, setSelected]);

    const flag = mapField ? value : selected;

    return <>
        {image ? <ElementIcon element={image} fontSize={iconSize} /> : null}
        {image ? ' ' : null}
        {checkbox ? (<button
            className={`icon-check-field btn ${flag ? 'btn-success' : 'btn-secondary'}`}
            color="secondary"
            onClick={onClick}
        >{flag ? <i className="fa fa-check" /> : null}</button>) : null}
        {type === 'badge' ? (
            <Badge color={color} style={{ fontSize: "100%" }} pill>
                {text || emptyText}
            </Badge>
        ) : (
            `${title}${title ? ":" : ""} ${text}` || emptyText || ""
        )}
    </>;
}


function DataCell({ rowObject, cell, selected, setSelected }) {
    const {
        'map:field': mapField,
        'map:image': mapImage,
        iconSize,
        format,
        type,
        checkbox,
        color: colorExpr,
        valueTranform: valueTranformExpr,
        isHTML = false
    } = cell;
    const emptyText = cell.emptyText || (checkbox ? '' : '---');

    const colorJnx = useJnx(colorExpr);
    const valueTranformJnx = useJnx(valueTranformExpr);

    const value = useMemo(() => {
        let value = mapField ? mapObject(rowObject, { "value": mapField }).value : null;

        if (valueTranformJnx) value = valueTranformJnx.eval(rowObject, '', { value })

        return value;
    }, [rowObject, mapField, valueTranformJnx]);

    const color = useMemo(() => colorJnx?.eval(rowObject, '', { value }), [value, colorJnx, rowObject]);
    const formatter = useFormatter(format);
    const text = useMemo(() => (
        formatter !== IDENTITY_FORMATTER ? (
            formatter.apply(value)
        ) : (value !== null && value !== undefined ? (
            `${value}`
        ) : ''
        )), [value, formatter]);
    const image = useMemo(() => mapImage ? mapObject(rowObject, { "value": mapImage }).value : null, [rowObject, mapImage]);

    const onClick = useCallback((e) => {
        e.preventDefault();
        setSelected(rowObject);
    }, [rowObject, setSelected]);

    const flag = mapField ? value : selected;

    return <>
        {image ? <ElementIcon element={image} fontSize={iconSize} /> : null}
        {image ? ' ' : null}
        {checkbox ? (<button
            className={`icon-check-field btn ${flag ? 'btn-success' : 'btn-secondary'}`}
            color="secondary"
            onClick={onClick}
        >{flag ? <i className="fa fa-check" /> : null}</button>) : null}
        {type === 'badge' ? (
            <Badge color={color} style={{ fontSize: "100%" }} pill>
                {text || emptyText}
            </Badge>
        ) :  type === 'square-badge' ? (
            <Badge color={color} style={{ fontSize: "100%" }} >
                {text || emptyText}
            </Badge>
        ) : (
            isHTML ? HTMLCodeDisplay(text) :
                text || emptyText || ""
        )}
    </>;
}

const HTMLCodeDisplay = (htmlCode) => {
    return <div dangerouslySetInnerHTML={{ __html: `<div style="font-family: 'Inter'">${htmlCode}</div>` }} />;
};

const BUTTONS_COMPONENTS = {
    'ConfirmButton': ConfirmButton
};

function ActionButtonsCell({ actions, rowObject, scope, fnBindings }) {
    const auth = useContext(AuthContext);

    const buttons = actions.map(obj => {
        const newObj = { ...obj };
        if (obj.requireExpr) {
            newObj.requireJnx = useJnx(obj.requireExpr);
        }
        return newObj;
    });

    const filteredActions = useMemo(() => buttons.filter(obj => {
        return (!obj.requireJnx || obj.requireJnx.eval(scope)) &&
            (!obj.require || obj.require.some((requirement) => checkButtonAccess(requirement, rowObject, auth))) &&
            (!obj.restrict || obj.restrict.every((requirement) => !checkButtonAccess(requirement, rowObject, auth)))
    }), [auth, buttons]);

    return <>{filteredActions.map((action, idx) => {
        const Component = BUTTONS_COMPONENTS[action?.type] || ActionButton;
        return <Component fn={fnBindings} key={idx} action={action} rowObject={rowObject} scope={scope} />
    })}</>;
}

function checkButtonAccess({ role, permission, flag }, rowObject, auth) {
    return (role ? checkAccess(auth, role, permission) : true) && (flag ? getObject(rowObject, flag) : true)
}


function ActionButton({ action, rowObject, scope: propScope }) {
    const scope = useMemo(() => ({
        id: rowObject?.id,
        entityTypeSlug: rowObject?.entityTypeSlug,
        item: rowObject,
        ...propScope
    }), [rowObject, propScope]);

    const route = useRouteInterpolation(action.route, scope);

    let dataCy = `btn_${action.label}`;
    if (scope?.id) dataCy += `_${scope.id}`;

    return (
        action.openInNewTab ? 
        <button
            style={{ 'backgroundColor': action.backgroundColor ? action.backgroundColor : null }}
            className="btn btn-primary"
            data-cy={dataCy}
            href={route}
            onClick={e => {
                e.preventDefault();
                window.open(route, "_blank", "noopener,noreferrer");
            }}
        >{action.label}</button>
        :
        <Link
            style={{ 'backgroundColor': action.backgroundColor ? action.backgroundColor : null }}
            className="btn btn-primary"
            data-cy={dataCy}
            to={route}
            rel="noopener noreferrer"
        >{action.label}</Link>
    );
}

async function onConfirm({ confirm, fn }) {

    const WORKFLOW_SELECT = () => {
        return [
            "ConfirmationModal",
            confirm?.title || ``,
            confirm?.text || ``
        ]
    };

    const wf = (await fn.openModal(...WORKFLOW_SELECT()));
    if (wf) {
        fn.loaderFnAction(true);
        const response = await UweApi[confirm.method]({ api: confirm.api });
        if (response) fn.onSearch(); fn.loaderFnAction(false);
    }
}

function ConfirmButton({ action, rowObject, scope: propScope, fn }) {
    const scope = useMemo(() => ({
        id: rowObject?.id,
        entityTypeSlug: rowObject?.entityTypeSlug,
        item: rowObject,
        ...propScope
    }), [rowObject, propScope]);

    const api = interpolate(action.confirm.api, scope);

    let dataCy = `btn_confirm_${action.label}`;
    if (scope?.id) dataCy += `_${scope.id}`;

    return (<Button
        className="btn btn-primary"
        color=""
        data-cy={dataCy}
        onClick={() => onConfirm({ fn, confirm: { ...action?.confirm, api } })}
    >{action.label}</Button>);
}

const parseSearchFormDefinition = ({ filters }) => {
    if (filters) {
        const sFilters = {};
        const schema = filters.reduce((_, { key, ...obj }) => {
            _[key] = obj;
            return _;
        }, {});
        return [parseFormDefinition({
            schemaProps: {
                ":classNames": "smallThreeRows",
            },
            schema
        }), sFilters];
    }
    return [];
};

export default WorkTray;